import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faHome,
    faLandmark,
    faFileContract,
    faHandshake,
    faScaleBalanced,
    faGavel,
    faUsers,
    faShieldAlt,
    faLightbulb,
    faAward,
    faCheckCircle,
    faChevronRight,
    faBalanceScale,
    faGlobe,
    faMoneyBillWave,
    faCity,
    faBuildingColumns,
    faFileSignature,
    faLandmarkFlag,
    faClock
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const services = [
    {
        icon: faFileContract,
        title: "Property Transactions",
        description: "Due diligence, title verification, documentation, and registration for property transactions.",
        color: "gold",
        subservices: [
            "Title Verification & Search",
            "Sale Agreement Drafting",
            "Registration & Stamp Duty",
            "Mutation & Conveyance"
        ],
        stat: "500+"
    },
    {
        icon: faLandmark,
        title: "Land Acquisition",
        description: "Assistance in government and private land acquisition, compensation claims, and rehabilitation.",
        color: "primary-blue",
        subservices: [
            "Land Acquisition Cases",
            "Compensation Claims",
            // "Rehabilitation Matters",
            // "RERA Compliance"
        ],
        stat: "100+"
    },
    {
        icon: faBuilding,
        title: "Real Estate Development",
        description: "Legal support for real estate projects from planning to completion and sale.",
        color: "gold",
        subservices: [
            // "Project Planning & Approvals",
            "Builder-Buyer Agreements",
            // "RERA Registration",
            // "Project Finance"
        ],
        stat: "50+"
    },
    {
        icon: faGavel,
        title: "Property Disputes",
        description: "Resolution of property disputes through litigation, arbitration, and mediation.",
        color: "primary-blue",
        subservices: [
            "Partition Suits",
            "Specific Performance",
            "Title Disputes",
            "Eviction Suits"
        ],
        stat: "300+"
    },
    {
        icon: faUsers,
        title: "NRI Property Matters",
        description: "Specialized legal services for NRIs dealing with Indian property matters.",
        color: "gold",
        subservices: [
            // "NRI Property Investment",
            "Power of Attorney",
            "Succession & Inheritance",
            // "Property Management"
        ],
        stat: "200+"
    },
    {
        icon: faShieldAlt,
        title: "Leasing & Tenancy",
        description: "Commercial and residential lease agreements, tenancy disputes, and eviction matters.",
        color: "primary-blue",
        subservices: [
            "Lease Agreement Drafting",
            "Rent Control Matters",
            "Eviction Proceedings",
            "License Agreements"
        ],
        stat: "150+"
    }
];

const propertyTypes = [
    {
        type: "Residential Properties",
        icon: faHome,
        description: "Apartments, independent houses, villas, and residential plots",
        matters: ["Sale/Purchase", "Society Formation", "Maintenance", "Succession"],
        color: "gold"
    },
    {
        type: "Commercial Properties",
        icon: faBuilding,
        description: "Office spaces, shops, malls, and commercial complexes",
        matters: ["Lease Agreements", "Mall Management", "Commercial Leases", "Franchise"],
        color: "primary-blue"
    },
    {
        type: "Industrial Properties",
        icon: faCity,
        description: "Factories, warehouses, industrial sheds, and manufacturing units",
        matters: ["Industrial Leases", "Pollution Clearance", "Factory Act", "Zoning"],
        color: "gold"
    },
    {
        type: "Agricultural Land",
        icon: faLandmarkFlag,
        description: "Farmland, plantations, orchards, and agricultural estates",
        matters: ["Tenancy Rights", "Land Ceiling", "Conversion", "Agriculture Laws"],
        color: "primary-blue"
    }
];

const legalActs = [
    {
        act: "Transfer of Property Act, 1882",
        icon: faFileSignature,
        description: "Governs transfer of immovable property through sale, mortgage, lease, gift, and exchange.",
        relevance: "Core property transfer law"
    },
    {
        act: "Registration Act, 1908",
        icon: faBuildingColumns,
        description: "Mandatory registration of documents related to immovable property transactions.",
        relevance: "Document registration"
    },
    {
        act: "Indian Stamp Act, 1899",
        icon: faMoneyBillWave,
        description: "Governs stamp duty on property transactions and related documents.",
        relevance: "Stamp duty compliance"
    },
    {
        act: "Real Estate (Regulation) Act, 2016",
        icon: faShieldAlt,
        description: "Regulates real estate sector and protects home buyers' interests.",
        relevance: "RERA compliance"
    },
    {
        act: "Land Acquisition Act, 2013",
        icon: faLandmark,
        description: "Governs acquisition of land for public purposes and private companies.",
        relevance: "Land acquisition"
    },
    {
        act: "Specific Relief Act, 1963",
        icon: faScaleBalanced,
        description: "Provides remedies for breach of contract including specific performance.",
        relevance: "Property contract enforcement"
    }
];

const caseStudies = [
    {
        title: "High-Value Residential Complex Dispute",
        client: "Prominent Builder",
        issue: "₹150 Cr project with multiple buyer complaints",
        solution: "Mediation leading to settlement",
        result: "Project completion with satisfied buyers",
        duration: "6 months",
        value: "₹150 Crores"
    },
    {
        title: "Agricultural Land Acquisition",
        client: "Government Agency",
        issue: "Compensation dispute for 50 acres",
        solution: "Arbitration proceedings",
        result: "Enhanced compensation awarded",
        duration: "8 months",
        value: "₹25 Crores"
    },
    {
        title: "NRI Property Inheritance",
        client: "NRI Family",
        issue: "Complex succession involving multiple heirs",
        solution: "Comprehensive legal documentation",
        result: "Smooth property transfer",
        duration: "3 months",
        value: "Family Estate Preserved"
    },
    {
        title: "Commercial Lease Dispute",
        client: "Retail Chain",
        issue: "Mall lease termination conflict",
        solution: "Negotiated settlement",
        result: "Lease terms renegotiated",
        duration: "2 months",
        value: "Business Continuity"
    }
];

const reraServices = [
    {
        service: "Appeals Before REAT",
        description: "Legal representation and assistance in filing appeals before the Real Estate Appellate Tribunal (REAT)",
        icon: faGavel,
        color: "gold"
    }
      ,    
    {
        service: "Buyer Complaints",
        description: "Representation before RERA authorities for aggrieved buyers",
        icon: faUsers,
        color: "primary-blue"
    },
    {
        service: "Compliance Advisory",
        description: "Ensuring ongoing compliance with RERA regulations",
        icon: faCheckCircle,
        color: "gold"
    },
    {
        service: "Promoter Representation",
        description: "Legal representation for builders and developers",
        icon: faBuilding,
        color: "primary-blue"
    }
];

export const RealEstateLaw = () => {
    const [activeService, setActiveService] = useState(0);
    const [hoveredType, setHoveredType] = useState(null);

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Property Law Specialists
                        </span>


                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Real Estate & Property Law Services
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Comprehensive legal solutions for property transactions, disputes, development,
                        and compliance. Trust our Team for all your
                        real estate legal needs.
                    </motion.p>
                </motion.div>

                {/* Services Section */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBuilding} className="me-3" style={{ color: "var(--gold)" }} />
                            Our Property Law Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            End-to-end legal solutions for all real estate matters
                        </p>
                    </div>

                    <div className="row g-4">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveService(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                    color: `var(--${service.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            {/* <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${service.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeService === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {service.stat}
                                            </motion.div> */}
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {service.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {service.description}
                                        </p>

                                        {/* Subservices */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${service.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {service.subservices.map((subservice, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${service.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {subservice}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Property Types */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faHome} className="me-3" style={{ color: "var(--gold)" }} />
                            Property Disputes We Handle
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expertise across diverse property categories
                        </p>
                    </div>

                    <div className="row g-4">
                        {propertyTypes.map((property, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setHoveredType(index)}
                                onMouseLeave={() => setHoveredType(null)}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100 text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${property.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${property.color})`
                                    }}
                                >
                                    <motion.div
                                        className="mb-3"
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${property.color}-rgb), 0.1)`,
                                                color: `var(--${property.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={property.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </motion.div>

                                    <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {property.type}
                                    </h5>

                                    <p className="small mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {property.description}
                                    </p>

                                    <div className="vstack gap-2">
                                        {property.matters.map((matter, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2 justify-content-center">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: `var(--${property.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {matter}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* RERA Services */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faShieldAlt} className="me-3" style={{ color: "var(--gold)" }} />
                            RERA Compliance Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expert guidance on Real Estate Regulation Act compliance
                        </p>
                    </div>

                    <div className="row g-4">
                        {reraServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                color: `var(--${service.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {service.service}
                                    </h5>

                                    <p className="small text-center mb-0" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {service.description}
                                    </p>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Legal Acts & Statutes */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBalanceScale} className="me-3" style={{ color: "var(--gold)" }} />
                            Key Property Laws & Acts
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expertise in major property legislation
                        </p>
                    </div>

                    <div className="row g-4">
                        {legalActs.map((act, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)"
                                    }}
                                >
                                    <div className="d-flex align-items-start gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={act.icon} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                                {act.act}
                                            </h6>
                                            <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
                                                {act.description}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <span className="badge px-3 py-1"
                                            style={{
                                                backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                                                color: "var(--primary-blue)",
                                                fontSize: "0.75rem"
                                            }}
                                        >
                                            {act.relevance}
                                        </span>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Case Studies */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Property Law Case Studies
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Successful resolutions in complex property matters
                        </p>
                    </div>

                    <div className="row g-4">
                        {caseStudies.map((caseStudy, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            {caseStudy.title}
                                        </h6>
                                        <div className="small mb-3" style={{ color: "var(--gold)" }}>
                                            Client: {caseStudy.client}
                                        </div>
                                    </div>

                                    <div className="vstack gap-3 mb-4">
                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Issue
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.issue}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Solution
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.solution}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Result
                                            </div>
                                            <div className="small fw-bold" style={{ color: "var(--green-accent)" }}>
                                                {caseStudy.result}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <div className="d-flex align-items-center gap-1">
                                            <FontAwesomeIcon
                                                icon={faClock}
                                                style={{
                                                    color: "var(--primary-blue)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.duration}
                                            </span>
                                        </div>

                                        <div className="fw-bold" style={{ color: "var(--gold)" }}>
                                            {caseStudy.value}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Our Approach */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faLightbulb} className="me-3" style={{ color: "var(--gold)" }} />
                                        Our Property Law Approach
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faFileContract}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Comprehensive Due Diligence
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Thorough title verification and legal checks
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faHandshake}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Transaction Management
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        End-to-end guidance through property transactions
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faGavel}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Dispute Resolution
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Litigation, arbitration, and mediation options
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faGlobe}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        NRI Specialization
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Expertise in cross-border property matters
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faBuilding}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        {/* <div className="display-6 fw-bold mb-2" style={{ color: "var(--white)" }}>
                                            1000+
                                        </div> */}
                                        <div style={{ color: "var(--gold)" }}>
                                        Handles all types of Property Matters
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                
                <Cta Title="Need Property Law Expertise?" description="Contact our property law specialists for a confidential consultation on your real estate legal matters."/>
            </div>
        </div>
    );
};